<template>
  <div class="onboarding-wizard">
    <wizard-progress-bar
      v-if="!isWelcomeStep"
      :current="progressCurrent"
      :total="progressTotal"
    />

    <component
      :is="currentStepComponent"
      :wizard-data="wizardData"
      @update:wizardData="onWizardDataUpdate"
    />

    <wizard-nav
      :is-first-step="isWelcomeStep"
      :is-last-step="isLastStep"
      :can-advance="canAdvance"
      :can-skip="showSkip"
      :submit-label="submitLabel"
      :loading="loading"
      @back="handleBack"
      @next="handleNext"
      @skip="handleSkip"
    />

    <v-alert v-if="submitError" type="error" dense class="mt-3">
      {{ submitError }}
    </v-alert>
  </div>
</template>

<script>
import { gardenApi, gardenVarietyApi, profileApi, varietiesApi } from '@/services/api'
import WizardProgressBar from './WizardProgressBar.vue'
import WizardNav from './WizardNav.vue'
import StepWelcome from './steps/StepWelcome.vue'
import StepLocation from './steps/StepLocation.vue'
import StepGoals from './steps/StepGoals.vue'
import StepVarieties from './steps/StepVarieties.vue'
import StepConfirm from './steps/StepConfirm.vue'

const DRAFT_KEY = 'onboardingDraft'
const GARDEN_ID_KEY = 'jardin_garden_id'

const EMPTY_WIZARD_DATA = () => ({
  name: '',
  city: '',
  exposition: 'full_sun',
  container_only: true,
  experience_level: null,
  goals: [],
  selectedCategories: [],
  has_worm_compost: false,
})

export default {
  name: 'OnboardingWizard',
  components: { WizardProgressBar, WizardNav, StepWelcome, StepLocation, StepGoals, StepVarieties, StepConfirm },

  props: {
    mode: {
      type: String,
      default: 'create',
      validator: (v) => ['create', 'edit'].includes(v),
    },
  },

  data() {
    return {
      currentStep: 1,
      wizardData: EMPTY_WIZARD_DATA(),
      loading: false,
      submitError: null,
    }
  },

  computed: {
    steps() {
      const all = ['StepWelcome', 'StepLocation', 'StepGoals', 'StepVarieties', 'StepConfirm']
      if (this.mode === 'edit') return all.slice(1)
      return all
    },
    currentStepName() {
      return this.steps[this.currentStep - 1]
    },
    currentStepComponent() {
      const map = { StepWelcome, StepLocation, StepGoals, StepVarieties, StepConfirm }
      return map[this.currentStepName]
    },
    isWelcomeStep() {
      return this.currentStepName === 'StepWelcome'
    },
    isLastStep() {
      return this.currentStep === this.steps.length
    },
    progressTotal() {
      return this.steps.filter((s) => s !== 'StepWelcome').length
    },
    progressCurrent() {
      const nonWelcome = this.steps.filter((s) => s !== 'StepWelcome')
      const idx = nonWelcome.indexOf(this.currentStepName)
      return idx + 1
    },
    canAdvance() {
      if (this.currentStepName === 'StepLocation') {
        return !!(this.wizardData.name && this.wizardData.city)
      }
      return true
    },
    // Goals and Varieties steps have an optional Skip
    showSkip() {
      return this.currentStepName === 'StepGoals' || this.currentStepName === 'StepVarieties'
    },
    submitLabel() {
      return this.mode === 'edit' ? 'Save changes' : 'Create my garden'
    },
  },

  created() {
    if (this.mode === 'edit') {
      this.prefillFromStore()
    } else {
      this.restoreDraft()
    }
  },

  watch: {
    wizardData: {
      deep: true,
      handler(val) {
        if (this.mode === 'create') {
          localStorage.setItem(DRAFT_KEY, JSON.stringify(val))
        }
      },
    },
  },

  methods: {
    onWizardDataUpdate(updated) {
      this.wizardData = updated
    },

    prefillFromStore() {
      const garden = this.$store.state.garden?.garden
      if (garden) {
        this.wizardData = {
          ...EMPTY_WIZARD_DATA(),
          name: garden.name || '',
          city: garden.city || '',
          exposition: garden.exposition || 'full_sun',
          container_only: garden.container_only ?? true,
          has_worm_compost: garden.has_worm_compost ?? false,
        }
      }
    },

    restoreDraft() {
      try {
        const raw = localStorage.getItem(DRAFT_KEY)
        if (raw) {
          const parsed = JSON.parse(raw)
          this.wizardData = { ...EMPTY_WIZARD_DATA(), ...parsed }
        }
      } catch {
        // ignore corrupt draft
      }
    },

    clearDraft() {
      localStorage.removeItem(DRAFT_KEY)
    },

    handleBack() {
      if (this.currentStep > 1) this.currentStep--
    },

    handleNext() {
      if (this.isLastStep) {
        this.handleSubmit()
      } else {
        this.currentStep++
      }
    },

    handleSkip() {
      // Skip clears the optional data for this step then advances
      if (this.currentStepName === 'StepGoals') {
        this.wizardData = { ...this.wizardData, experience_level: null, goals: [] }
      } else if (this.currentStepName === 'StepVarieties') {
        this.wizardData = { ...this.wizardData, selectedCategories: [] }
      }
      this.currentStep++
    },

    async handleSubmit() {
      this.loading = true
      this.submitError = null

      const gardenPayload = {
        name: this.wizardData.name,
        city: this.wizardData.city,
        exposition: this.wizardData.exposition,
        container_only: this.wizardData.container_only,
        has_worm_compost: this.wizardData.has_worm_compost,
      }

      try {
        let gardenId

        if (this.mode === 'edit') {
          gardenId = localStorage.getItem(GARDEN_ID_KEY) || this.$store.state.garden?.garden?.id
          await gardenApi.update(gardenId, gardenPayload)
        } else {
          const { data } = await gardenApi.create(gardenPayload)
          gardenId = data.id
          localStorage.setItem(GARDEN_ID_KEY, gardenId)
        }

        // Phase B: persist profile (fire-and-forget)
        if (this.wizardData.experience_level || this.wizardData.goals.length) {
          profileApi
            .update({
              experience_level: this.wizardData.experience_level || '',
              goals: this.wizardData.goals,
            })
            .catch(() => {})
        }

        // Phase C: seed selected variety categories
        if (this.wizardData.selectedCategories.length) {
          await this.seedVarieties(gardenId, this.wizardData.selectedCategories)
        }

        this.clearDraft()
        await this.$store.dispatch('garden/loadDashboard', gardenId)
        this.$emit('done')
        this.$router.push('/').catch(() => {})
      } catch (err) {
        this.submitError = err.response?.data?.detail || 'Something went wrong. Please try again.'
      } finally {
        this.loading = false
      }
    },

    async seedVarieties(gardenId, categories) {
      const seeds = await Promise.all(
        categories.map((category) =>
          varietiesApi
            .list({ category, limit: 1 })
            .then(({ data }) => {
              const results = data.results ?? data
              return results.length ? results[0] : null
            })
            .catch(() => null)
        )
      )

      await Promise.all(
        seeds
          .filter(Boolean)
          .map((variety, idx) =>
            gardenVarietyApi
              .create({
                garden: gardenId,
                variety: variety.id,
                status: 'planned',
                priority: idx + 1,
              })
              .catch(() => {})
          )
      )
    },
  },
}
</script>

<style scoped>
.onboarding-wizard {
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  padding: 32px;
}
</style>
